// React
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

// Servisni sloj
import { getTenantAPI } from "services/api/tenants/api";
import { Tenant, TenantCreateFormValues } from "services/api/tenants/types";
import { getTenantSubscriptionBillingAPI } from "services/api/subscriptions/api";
import { initialSubscriptionStepForm } from "services/api/subscriptions/consts";
import {
  SubscriptionBilling,
  SubscriptionStepFormValues,
} from "services/api/subscriptions/types";

// UI komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { BreadcrumbItem } from "ui/components/Breadcrumb/types";
import { Button } from "ui/components/Button/Button";
import { Loader } from "ui/components/Loader";
import { NoResults } from "ui/components/NoResults";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import {
  TENANT_DETAIL_PAGE_TITLE,
  TENANT_SEARCH_PAGE_TITLE,
} from "ui/components/PageTitle/consts";

// Moduli specifični za projekt
import { Routes } from "ui/modules/navigation";
import TabMenu from "../TabMenu/TabMenu";

// Lokalne komponente
//
// Prikaz je ista komponenta koju čarobnjak koristi za unos, ne njezina preslika: tako je
// izgled identičan i ostaje takav kad se korak jednom promijeni.
import TenantSubscriptionStep from "ui/modules/tenants/pages/TenantsSearchPage/components/TenantEditCard/components/TenantSubscriptionStep/TenantSubscriptionStep";
import SubscriptionCalculatorModal from "./components/SubscriptionCalculatorModal/SubscriptionCalculatorModal";
import SubscriptionEditCard from "./components/SubscriptionEditCard/SubscriptionEditCard";

// Tipovi
import { TenantSubscriptionPageCmp, TenantSubscriptionPageProps } from "./types";

/** Prikaz ne mijenja vrijednosti; korak svejedno traži `setFormValues`. */
const noop = () => undefined;

/**
 * Obračun pretplate: koliko klijent plaća danas i zašto toliko.
 *
 * Nijedna brojka se ne računa ovdje. Poslužitelj vrti isti resolver i isti kalkulator kao pri
 * potpisu, samo sa stvarnim brojem vozila umjesto s pretpostavkom iz čarobnjaka.
 */
const TenantSubscriptionPage: TenantSubscriptionPageCmp = (
  _props: TenantSubscriptionPageProps
) => {
  const { tenantId } = useParams<{ tenantId: string }>();

  const [tenant, setTenant] = useState<Tenant>();
  const [billing, setBilling] = useState<SubscriptionBilling | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showCalculatorModal, setShowCalculatorModal] = useState<boolean>(false);

  const fetchAll = useCallback(async () => {
    if (!tenantId) return;

    setIsLoading(true);

    try {
      const [tenantResult, billingResult] = await Promise.all([
        getTenantAPI(tenantId),
        getTenantSubscriptionBillingAPI(tenantId),
      ]);

      setTenant(tenantResult);
      setBilling(billingResult);
    } catch {
      setBilling(null);
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleEditClick = useCallback(() => {
    setShowEditModal(true);
  }, []);

  const handleEditClose = useCallback(() => {
    setShowEditModal(false);
  }, []);

  const handleEditSuccess = useCallback(() => {
    setShowEditModal(false);
    fetchAll();
  }, [fetchAll]);

  const handleCalculatorClick = useCallback(() => {
    setShowCalculatorModal(true);
  }, []);

  const handleCalculatorClose = useCallback(() => {
    setShowCalculatorModal(false);
  }, []);

  const title = TENANT_DETAIL_PAGE_TITLE;

  const breadcrumbs: BreadcrumbItem[] = [
    { text: TENANT_SEARCH_PAGE_TITLE.text, route: TENANT_SEARCH_PAGE_TITLE.route },
  ];

  const tenantName = tenant
    ? tenant.companyName || [tenant.firstName, tenant.lastName].filter(Boolean).join(" ")
    : "";

  if (!tenantId) return <></>;

  const hasSubscription = billing?.subscription != null;

  /*
    Vrijednosti kojima se puni prikaz. Broj vozila je stvaran, pa se panel s iznosom u koraku
    poklapa s obračunom, a ne s pretpostavkom iz čarobnjaka.
  */
  const viewValues: SubscriptionStepFormValues = {
    ...initialSubscriptionStepForm,
    packCodes: billing?.subscription?.packCodes ?? [],
    billingPeriod: billing?.subscription?.billingPeriod ?? "Monthly",
    startsOn: billing?.subscription?.startsOn ?? initialSubscriptionStepForm.startsOn,
    withTrial: billing?.subscription?.trialEndsOn != null,
    estimatedVehicles: billing?.vehicleCount ?? 0,
  };

  return (
    <div>
      <div className="bg-white rounded-t-md">
        <PageHeader>
          <div className="flex items-center justify-between">
            <div>
              <PageTitle
                title={tenantName ? `${title.text} [${tenantName}]` : title.text}
                icon={title.icon}
                iconColor={title.iconColor}
              />
              <Breadcrumb items={breadcrumbs} />
            </div>

            {hasSubscription && (
              <div className="flex items-center gap-3">
                <Button
                  variant="outlined"
                  children="Izračunaj cijenu"
                  onClick={handleCalculatorClick}
                />

                <Button
                  variant="primary"
                  children="Promjeni"
                  startIcon="/media/icons/plus.svg"
                  onClick={handleEditClick}
                />
              </div>
            )}
          </div>
        </PageHeader>
        <TabMenu tenantId={tenantId} />
      </div>

      {!isLoading && !hasSubscription ? (
        <div className="px-4 py-6">
          <NoResults message="Klijent nema pretplatu. Ugovara se kroz uređivanje klijenta." />
        </div>
      ) : (
        billing?.subscription && (
          /*
            Ovo je ista komponenta koju čarobnjak koristi za unos, a ne njezina preslika, pa
            je prikaz doslovno identičan i ostaje takav kad se korak jednom promijeni.

            `pointer-events-none` je jedini razlog zašto se ovdje ne može ništa dirati:
            izgled ostaje netaknut, a mijenja se kroz modal iznad. `aria-hidden` drži
            čitače ekrana podalje od kontrola koje ne rade.
          */
          <div className="pointer-events-none select-none" aria-hidden="true">
            <TenantSubscriptionStep
              tenantValues={{ id: tenantId } as TenantCreateFormValues}
              formValues={viewValues}
              setFormValues={noop}
              existingSubscription={billing.subscription}
              isEditing
              showEstimate={false}
            />
          </div>
        )
      )}

      {billing?.subscription && (
        <SubscriptionEditCard
          show={showEditModal}
          tenantId={tenantId}
          subscription={billing.subscription}
          vehicleCount={billing.vehicleCount}
          onSuccess={handleEditSuccess}
          onClose={handleEditClose}
        />
      )}

      {billing?.subscription && (
        <SubscriptionCalculatorModal
          show={showCalculatorModal}
          tenantId={tenantId}
          subscription={billing.subscription}
          vehicleCount={billing.vehicleCount}
          onClose={handleCalculatorClose}
        />
      )}

      <Loader isLoading={isLoading} message="Učitavanje podataka…">
        {null}
      </Loader>
    </div>
  );
};

TenantSubscriptionPage.route = Routes.TenantSubscriptionPageRouter;

export default TenantSubscriptionPage;
